import React, { Fragment } from 'react';

import { faFilePdf, faFolderPlus, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ProgressBar from '@ramonak/react-progress-bar';
import { FileWithPath } from 'react-dropzone';

import { FileWithPreview, getUploadButtonText, thumbStyles, thumbInnerStyles, imgStyles } from './utils';
import Button from '@/components/atoms/Button';

interface FileUploadContentProps {
  isUploading: boolean;
  maxFiles?: number;
  showPreview?: boolean;
  successfulUploads: number;
  totalProgress?: number;
  acceptedFiles: readonly FileWithPath[];
  files: FileWithPreview[];
  onDeleteClick: (index: number) => void;
  isDragActive: boolean;
  type?: string;
}

const FileUploadContent: React.FC<FileUploadContentProps> = ({
  isUploading,
  isDragActive,
  maxFiles,
  showPreview,
  files,
  onDeleteClick,
  acceptedFiles,
  totalProgress,
  successfulUploads = 0,
  type = 'files',
}) => {
  const renderFilePreview = () => {
    return files.map((file, index) => {
      return file.type.includes('pdf') ? (
        <Fragment key={file.name}>
          <div className="relative mr-4 h-full w-12">
            <FontAwesomeIcon
              className="absolute -top-2 right-2 hover:text-gray-500"
              icon={faTimesCircle}
              onClick={() => onDeleteClick(index)}
            />
            <FontAwesomeIcon icon={faFilePdf} className="mr-4 h-1/3 text-gray-500 transition-transform duration-300" />
            <p className="truncate text-xs">{file.name}</p>
          </div>
        </Fragment>
      ) : (
        <div style={thumbStyles} key={file.name}>
          <div style={thumbInnerStyles}>
            <img
              src={file.preview}
              alt={file.name}
              style={imgStyles}
              onLoad={() => {
                URL.revokeObjectURL(file.preview);
              }}
            />
          </div>
        </div>
      );
    });
  };

  const uploadButtonText = getUploadButtonText(type, maxFiles);

  if (isDragActive) {
    if (maxFiles === files.length) {
      return <p className="text-red-400">Maximum amount of files reached, remove a file first</p>;
    }
    return <p className="text-blue-500">Drop the files here ...</p>;
  }

  if (isUploading) {
    return (
      <div className="flex flex-col">
        <div className="mb-4 text-center text-base leading-6 font-bold text-gray-900 not-italic">
          Uploaded {successfulUploads} of {acceptedFiles.length} files
        </div>
        <ProgressBar
          completed={totalProgress ?? 0}
          customLabel={''}
          bgColor={'#4161FF'}
          height={'6px'}
          className="animate-pulse"
          isLabelVisible={false}
        />
      </div>
    );
  }

  if (files.length && showPreview) {
    return <aside className="z-10 flex flex-1 justify-center">{renderFilePreview()}</aside>;
  }

  return (
    <div>
      <Button
        type="brand"
        testid="upload-files"
        rounded="md"
        text={uploadButtonText}
        onClick={() => null}
        icon={<FontAwesomeIcon icon={faFolderPlus} className="mr-2 text-white" />}
      />
      <p className="mt-3 text-center text-sm leading-4 text-gray-700 not-italic">Or drag here</p>
    </div>
  );
};

export default FileUploadContent;
